import { useEffect, useState, type FormEvent } from 'react';

import { ModalCloseButton } from './InfoModals';
import {
  formatModelLabel,
  type LaunchState,
  type LoadState,
  type PricingEstimateResponse,
  type PricingModelScanCost,
  type ScanAcceptedResponse
} from './workflowShared';

interface PricingStepProps {
  hasAcceptedScan: boolean;
  scanResponse: ScanAcceptedResponse | null;
  pricingEstimateState: LoadState;
  pricingEstimateError: string;
  pricingEstimate: PricingEstimateResponse | null;
  pricingEstimateFallback?: string;
  launchState: LaunchState;
  launchError: string;
  onViewRun: (runId: string) => void;
}

interface ResultEmailPromptModalProps {
  isOpen: boolean;
  initialEmail: string;
  launchState: LaunchState;
  onCancel: () => void;
  onSubmit: (email: string) => void;
}

const formatUsd = (value: number, options: Intl.NumberFormatOptions = {}): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  }).format(value);
};

const formatCompactUsd = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: value < 1 ? 3 : 2,
    maximumFractionDigits: value < 1 ? 3 : 2
  }).format(value);
};

const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

const formatMultiplier = (value: number): string => {
  return Number.isInteger(value) ? `${String(value)}x` : `${value.toFixed(2)}x`;
};

const getModelCostLines = (estimate: PricingEstimateResponse): PricingModelScanCost[] => {
  if (estimate.modelScanCosts.length === 0) {
    return [];
  }

  return estimate.modelScanCosts;
};

const getModelCostFallback = (estimate: PricingEstimateResponse): string => {
  if (estimate.modelMultipliers.length === 0) {
    return 'No models selected';
  }

  return estimate.modelMultipliers
    .map((entry) => `${formatModelLabel(entry.model)} ${formatMultiplier(entry.multiplier)}`)
    .join(', ');
};

const isValidResultEmail = (value: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

function ResultEmailPromptModal({
  isOpen,
  initialEmail,
  launchState,
  onCancel,
  onSubmit
}: ResultEmailPromptModalProps) {
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');
  const isSubmitting = launchState === 'submitting';

  useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail);
      setError('');
    }
  }, [initialEmail, isOpen]);

  if (!isOpen) {
    return null;
  }

  const submitEmail = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedEmail = email.trim();

    if (!isValidResultEmail(trimmedEmail)) {
      setError('Enter a valid email address.');
      return;
    }

    onSubmit(trimmedEmail);
  };

  const cancel = () => {
    if (!isSubmitting) {
      onCancel();
    }
  };

  return (
    <div className="modal-overlay" onClick={cancel}>
      <form
        className="modal-content pricing-email-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pricing-email-title"
        onClick={(event) => event.stopPropagation()}
        onSubmit={submitEmail}
      >
        <div className="modal-header">
          <h2 id="pricing-email-title">Get scan results</h2>
          <ModalCloseButton onClose={cancel} disabled={isSubmitting} />
        </div>

        <div className="modal-body pricing-email-body">
          <p className="pricing-email-copy">
            Add an email address so we can send the scan result when it is ready.
          </p>

          <label className="field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              autoComplete="email"
              autoFocus
              placeholder="security@example.com"
              disabled={isSubmitting}
              onChange={(event) => {
                setEmail(event.target.value);
                setError('');
              }}
            />
          </label>

          {error && (
            <p className="pricing-email-error" role="alert">{error}</p>
          )}
        </div>

        <div className="modal-footer pricing-email-footer">
          <button
            type="button"
            className="secondary-button"
            disabled={isSubmitting}
            onClick={cancel}
          >
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={isSubmitting}>
            {isSubmitting ? 'Starting...' : 'Start scan'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PricingStep({
  hasAcceptedScan,
  scanResponse,
  pricingEstimateState,
  pricingEstimateError,
  pricingEstimate,
  pricingEstimateFallback,
  launchState,
  launchError,
  onViewRun
}: PricingStepProps) {
  if (hasAcceptedScan) {
    const runId = scanResponse?.runId;

    return (
      <div className="pricing-launch-complete">
        <div className="pricing-launch-run-card" role="status" aria-label="Scan run created">
          <div className="pricing-launch-run-id">
            <span>Run ID</span>
            <strong>{runId || 'Unavailable'}</strong>
          </div>

          {runId ? (
            <a
              className="primary-button pricing-launch-run-link"
              href={`/run/${encodeURIComponent(runId)}`}
              onClick={(event) => {
                event.preventDefault();
                onViewRun(runId);
              }}
            >
              View run
            </a>
          ) : (
            <button className="primary-button pricing-launch-run-link" type="button" disabled>
              View run
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="step-heading pricing-heading">
        <h1>Free scan</h1>
        <p>
          Your daily free scan is ready. Estimated AI token cost is shown for transparency.
        </p>
      </div>

      {pricingEstimateState === 'loading' && (
        <div className="pricing-panel" role="status">
          <span className="pricing-muted">Estimating AI token cost...</span>
        </div>
      )}

      {pricingEstimateState === 'error' && (
        <div className="status-box error" role="alert">
          <span>AI token cost estimate failed.</span>
          <span>{pricingEstimateError}</span>
        </div>
      )}

      {pricingEstimateState === 'ready' && pricingEstimate && (
        <>
          <div className="pricing-panel">
            <div className="pricing-total-row">
              <span>Price of scan</span>
              <strong>{formatUsd(0)}</strong>
            </div>

            <div className="pricing-free-note">
              Estimated AI token cost: {formatUsd(pricingEstimate.totalUsd)}. Free scan allowance refreshes every 24 hours.
            </div>

            <dl className="pricing-breakdown">
              <div>
                <dt>Codebase</dt>
                <dd>
                  {formatNumber(pricingEstimate.codebase.nonCommentLineCount)} non-comment LOC in{' '}
                  {formatNumber(pricingEstimate.codebase.countedFileCount)} files
                </dd>
              </div>
              <div>
                <dt>Base token rate</dt>
                <dd>
                  {formatNumber(pricingEstimate.codebase.nonCommentLineCount)} ×{' '}
                  {formatCompactUsd(pricingEstimate.baseRateUsdPerNonCommentLine)} ={' '}
                  {formatCompactUsd(pricingEstimate.baseCodeCostUsd)}
                </dd>
              </div>
              <div>
                <dt>AI token cost</dt>
                <dd>
                  {getModelCostLines(pricingEstimate).length > 0 ? (
                    <span className="pricing-model-costs">
                      {getModelCostLines(pricingEstimate).map((entry) => (
                        <span key={`${entry.aiAgent}-${entry.model}`}>
                          {formatModelLabel(entry.model)}: {formatNumber(entry.scanRunCount)} scans ×{' '}
                          {formatMultiplier(entry.multiplier)} = {formatCompactUsd(entry.estimatedTokenCostUsd)}
                        </span>
                      ))}
                    </span>
                  ) : getModelCostFallback(pricingEstimate)}
                </dd>
              </div>
              <div>
                <dt>Skill runs</dt>
                <dd>
                  {formatNumber(pricingEstimate.scanRunCount)} total scans ({formatNumber(pricingEstimate.selectedModelCount)} models ×{' '}
                  {formatNumber(pricingEstimate.selectedSkillCount)} skills)
                </dd>
              </div>
              <div>
                <dt>Formula</dt>
                <dd>
                  Sum of {formatCompactUsd(pricingEstimate.baseCodeCostUsd)} × scans per model × model weight
                </dd>
              </div>
            </dl>
          </div>

        </>
      )}

      {pricingEstimateState === 'ready' && !pricingEstimate && pricingEstimateFallback && (
        <div className="pricing-panel">
          <div className="pricing-total-row">
            <span>Price of scan</span>
            <strong>{formatUsd(0)}</strong>
          </div>

          <div className="pricing-free-note">
            {pricingEstimateFallback}
          </div>
        </div>
      )}

      {launchState === 'error' && (
        <div className="status-box error" role="alert">
          <span>Scan request failed.</span>
          <span>{launchError}</span>
        </div>
      )}
    </>
  );
}

export { ResultEmailPromptModal };
export default PricingStep;
